import React, { createContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';

import { useAuth } from 'contexts/auth';
import { Icon, Stack } from 'shell/components';
import generator from 'generate-password';
import { emailRegex } from 'utils/constants';

const COUNTRY_OPTIONS = [
    {
        key: 'canada',
        value: (
            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                <Icon kind={'canada'} />
                Canada
            </Stack>
        ),
    },
    {
        key: 'usa',
        value: (
            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                <Icon kind={'usa'} />
                USA
            </Stack>
        ),
    },
];

const defaultForm = {
    email: '',
};

const defaultErrors = {
    email: '',
};

const PAGES = {
    CREATE_ACCOUNT: 'sign-up',
    VERIFY_EMAIL: 'verify-email',
};

const PageContext = createContext();

const PageProvider = (props) => {
    const history = useHistory();
    const { register, resendConfirmationCode, verifyUser } = useAuth();

    const [loading, setLoading] = useState(false);
    const [country, setCountry] = useState(COUNTRY_OPTIONS[0].key);
    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState(defaultErrors);
    const [currentStep, setCurrentStep] = useState(PAGES.CREATE_ACCOUNT);
    const [isExistingUser, setIsExistingUser] = useState(false);
    const [ToSModalOpen, setToSModalOpen] = useState({ tos: false, privacy: false });

    const handleSignUp = async () => {
        try {
            if (!validateAll()) return;
            setLoading(true);
            const tempPassword = generator.generate({
                length: 12,
                numbers: true,
                symbols: true,
                uppercase: true,
                lowercase: true,
                strict: true,
            });
            const signUp = await register({ username: form.email, password: tempPassword });
            if (signUp === 'UsernameExistsException') {
                setErrors((prev) => ({ ...prev, email: 'This email is already in use' }));
            } else if (signUp === 'Unconfirmed') {
                setErrors((prev) => ({ ...prev, email: 'Please confirm the email.' }));
            } else {
                setErrors((prev) => ({ ...prev, email: '' }));
                setCurrentStep(PAGES.VERIFY_EMAIL);
            }
            setLoading(false);
            return signUp;
        } catch (e) {
            console.log(e);
            setLoading(false);
            return false;
        }
    };

    const handleConfirmationCode = async (code) => {
        try {
            const verifyTheUser = await verifyUser(form.email, code);
            if (verifyTheUser === 'SUCCESS') return history.push('/');
        } catch (e) {
            console.error(e);
        }
    };

    const handleResendCode = async () => {
        return resendConfirmationCode(form.email);
    };

    const handleChange = async (key, callback) => {
        switch (key) {
            case 'email': {
                return setForm((prev) => ({ ...prev, email: callback }));
            }
            case 'country': {
                const currentUrl = window.location.origin;
                setCountry(callback);
                if (callback === 'canada' && currentUrl !== 'https://app.ensightful.ca') {
                    return window.location.replace('https://app.ensightful.ca/sign-up');
                } else if (callback === 'usa' && currentUrl !== 'https://app.ensightful.us') {
                    return window.location.replace('https://app.ensightful.us/sign-up');
                }
            }
            default:
                return;
        }
    };

    const getErrorMessage = (key, value) => {
        value = value?.trim();
        switch (key) {
            case 'email':
                return value && emailRegex.test(value) ? '' : 'Please enter a valid email.';
            default:
                return '';
        }
    };

    const validate = (key, value) => {
        const errorMessage = getErrorMessage(key, value);
        setErrors((errors) => {
            return {
                ...errors,
                [key]: errorMessage,
            };
        });
    };

    const validateAll = () => {
        return !Boolean(
            Object.keys(errors)
                .map((key) => {
                    validate(key, form[key]);
                    return getErrorMessage(key, form[key]);
                })
                .reduce((cur, acc) => {
                    return cur + acc;
                }, '')
        );
    };

    useEffect(() => {
        if (form.email?.length > 0) {
            setForm((prev) => {
                return { ...prev, email: form.email };
            });
        }
    }, [form?.email]);

    useEffect(() => {
        const region = process.env.REACT_APP_AWS_REGION;
        if (region === 'ca-central-1') {
            setCountry(COUNTRY_OPTIONS[0].key);
        } else if (region === 'us-east-1') {
            setCountry(COUNTRY_OPTIONS[1].key);
        } else {
            setCountry(COUNTRY_OPTIONS[1].key);
        }
    }, []);

    return (
        <PageContext.Provider
            value={{
                loading,
                country,
                form,
                errors,
                currentStep,
                isExistingUser,
                ToSModalOpen,

                handleChange,
                handleConfirmationCode,
                handleSignUp,
                handleResendCode,
                setErrors,
                setForm,
                setToSModalOpen,
                PAGES,
                COUNTRY_OPTIONS,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
